import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { MessageSquare, RefreshCw, Loader2 } from "lucide-react";

const REFRESH_INTERVAL_MS = 60_000;

/**
 * ReviewsBackfillTile — progress card for the `wua-reviews-backfill`
 * supervised daemon. Shows narrated-degree coverage per country and
 * the daemon's recent output so we can tell at a glance that reviews
 * are being written in the background.
 */
export default function ReviewsBackfillTile() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/reviews/backfill-progress");
      setData(r.data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load review progress");
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load]);

  if (err) {
    return (
      <div
        data-testid="reviews-backfill-tile-error"
        className="border border-rose-200 bg-rose-50/50 rounded-xl p-4 text-sm text-rose-900"
      >
        Reviews backfill tile failed: {err}
      </div>
    );
  }
  if (!data) {
    return (
      <div
        data-testid="reviews-backfill-tile-loading"
        className="border border-stone-200 rounded-xl p-4 text-sm text-stone-500"
      >
        <Loader2 className="inline h-4 w-4 animate-spin mr-1" />
        Loading review coverage…
      </div>
    );
  }

  return (
    <div
      data-testid="reviews-backfill-tile"
      className="border border-stone-200 rounded-xl p-5 bg-white shadow-sm"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-emerald-50">
            <MessageSquare className="h-5 w-5 text-emerald-700" />
          </div>
          <div>
            <div className="text-sm font-semibold text-stone-900">
              Editorial reviews backfill
            </div>
            <div className="text-xs text-stone-500">
              Daemon: <code>wua-reviews-backfill</code> ·{" "}
              {data.generated_total.toLocaleString()} generated so far
            </div>
          </div>
        </div>
        <button
          data-testid="reviews-backfill-refresh-btn"
          onClick={load}
          className="text-xs text-stone-500 hover:text-stone-900 flex items-center gap-1"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        {(data.by_country || []).map((c) => (
          <div
            key={c.country}
            data-testid={`reviews-backfill-${c.country.toLowerCase()}`}
            className="rounded-lg border border-stone-100 p-3"
          >
            <div className="text-xs text-stone-500 mb-1">
              {c.country} narrated editorial degrees
            </div>
            <div className="text-2xl font-semibold text-stone-900">
              {c.with_reviews.toLocaleString()} / {c.narrated.toLocaleString()}
            </div>
            <div className="mt-2 h-1.5 bg-stone-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all"
                style={{ width: `${Math.min(100, c.coverage_pct)}%` }}
              />
            </div>
            <div className="text-[11px] text-stone-500 mt-1">
              {c.coverage_pct}% coverage
            </div>
          </div>
        ))}
      </div>

      {(data.recent || []).length > 0 && (
        <div className="mt-4">
          <div className="text-xs text-stone-500 mb-2">Latest 3 reviews</div>
          <ul className="space-y-1">
            {data.recent.map((r, i) => (
              <li
                key={i}
                className="text-xs flex items-center gap-2 text-stone-700"
              >
                <span className="inline-block w-8 text-center rounded bg-amber-100 text-amber-800 text-[10px] font-medium py-0.5">
                  {r.overall_rating}★
                </span>
                <span className="text-stone-400">{r.reviewer_display_name}</span>
                <span className="truncate flex-1">{r.degree_id}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="mt-4 text-[11px] text-stone-500 leading-relaxed">
        Pace: 8 degrees × 3 reviews per 30-min tick (~380/day). At current
        rate the reviewless backlog clears in about 2 weeks. All rows are
        tagged <code>source="editorial"</code>.
      </p>
    </div>
  );
}
